import { useQuery } from '@tanstack/react-query'
import type { ComponentType } from 'react'

import { LoadingRows } from '@/components/skeletons'
import {
  currenciesKey,
  fetchCurrencies,
  fetchManufacturers,
  fetchSuppliers,
  fetchUnits,
  manufacturersKey,
  suppliersKey,
  unitsKey,
} from '@/features/reference/api'
import { CurrenciesScreen } from '@/features/reference/CurrenciesScreen'
import { ManufacturersScreen } from '@/features/reference/ManufacturersScreen'
import { SuppliersScreen } from '@/features/reference/SuppliersScreen'
import { UnitsScreen } from '@/features/reference/UnitsScreen'
import { useSettings } from '@/features/settings/api'
import { flatten, TREE_API, treeKey, type TreeKind } from '@/features/trees/api'

import { AttachmentTypesBody, CategoriesBody, FootprintsBody } from './tree-bodies'

/** The seven kinds of reference data, in the order the catalogue rail lists them. */
export const CATALOGUE_KINDS = [
  'categories',
  'footprints',
  'manufacturers',
  'suppliers',
  'units',
  'currencies',
  'attachment-types',
] as const

export type CatalogueKind = (typeof CATALOGUE_KINDS)[number]

export function isCatalogueKind(value: string): value is CatalogueKind {
  return (CATALOGUE_KINDS as readonly string[]).includes(value)
}

interface KindInfo {
  title: string
  description: string
  Body: ComponentType
}

function CurrenciesBody() {
  const settings = useSettings()
  return settings.data ? (
    <CurrenciesScreen baseCurrency={settings.data.base_currency} />
  ) : (
    <LoadingRows label="Loading settings" />
  )
}

export const CATALOGUE: Record<CatalogueKind, KindInfo> = {
  categories: {
    title: 'Categories',
    description: 'How parts are classified.',
    Body: CategoriesBody,
  },
  footprints: { title: 'Footprints', description: 'Physical packages.', Body: FootprintsBody },
  manufacturers: {
    title: 'Manufacturers',
    description: 'Who makes the parts.',
    Body: ManufacturersScreen,
  },
  suppliers: {
    title: 'Suppliers',
    description: 'Where the parts are bought.',
    Body: SuppliersScreen,
  },
  units: { title: 'Units', description: 'How quantities are counted.', Body: UnitsScreen },
  currencies: {
    title: 'Currencies',
    description: 'Exchange rates against the base currency.',
    Body: CurrenciesBody,
  },
  'attachment-types': {
    title: 'Attachment types',
    description: 'How files are classified.',
    Body: AttachmentTypesBody,
  },
}

const TREE_OF: Partial<Record<CatalogueKind, TreeKind>> = {
  categories: 'categories',
  footprints: 'footprints',
  'attachment-types': 'attachment-types',
}

const LIST_OF = {
  manufacturers: { queryKey: manufacturersKey, queryFn: fetchManufacturers },
  suppliers: { queryKey: suppliersKey, queryFn: fetchSuppliers },
  units: { queryKey: unitsKey, queryFn: fetchUnits },
  currencies: { queryKey: currenciesKey, queryFn: fetchCurrencies },
} as const

/**
 * How many entries a kind has, for the rail. Reads through the same query keys
 * the screens use, so the open screen's count is free and an edit updates both.
 */
export function useCatalogueCount(kind: CatalogueKind): number | undefined {
  const tree = TREE_OF[kind]
  const treeQuery = useQuery({
    queryKey: tree ? treeKey(tree) : ['catalogue-none'],
    queryFn: async () => (tree ? await TREE_API[tree].tree() : []),
    enabled: tree !== undefined,
  })
  const list = kind in LIST_OF ? LIST_OF[kind as keyof typeof LIST_OF] : undefined
  const listQuery = useQuery({
    queryKey: list ? list.queryKey : ['catalogue-none'],
    queryFn: async (): Promise<unknown[]> => (list ? await list.queryFn() : []),
    enabled: list !== undefined,
  })
  if (tree) return treeQuery.data ? flatten(treeQuery.data).length : undefined
  return listQuery.data?.length
}
